/**
 * Settings Context
 *
 * Provides application settings throughout the app with localStorage persistence.
 * Uses a reducer pattern for state management.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

// Types
export type Theme = 'dark' | 'light' | 'system';

export interface Settings {
  theme: Theme;
  sidebarCollapsed: boolean;
  compactMode: boolean;
  notifications: {
    email: boolean;
    push: boolean;
    desktop: boolean;
  };
  accessibility: {
    reducedMotion: boolean;
    highContrast: boolean;
  };
  /** Persisted Library (assets) tier filter — UPPERCASE tier code, null = All. */
  libraryTierFilter: string | null;
  /** Learn / Browse-Courses active filter axis. */
  coursesFilterMode: 'pillars' | 'community';
  /** Persisted Learn pillar (server-side) — lowercase pillar, null = All. */
  coursesPillar: string | null;
  /** Persisted Learn tier (client-side) — UPPERCASE tier code, null = All. */
  coursesTier: string | null;
}

// Action types
type SettingsAction =
  | { type: 'SET_THEME'; payload: Theme }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_COLLAPSED'; payload: boolean }
  | { type: 'TOGGLE_COMPACT_MODE' }
  | { type: 'SET_NOTIFICATION'; payload: { key: keyof Settings['notifications']; value: boolean } }
  | { type: 'SET_ACCESSIBILITY'; payload: { key: keyof Settings['accessibility']; value: boolean } }
  | { type: 'SET_LIBRARY_TIER_FILTER'; payload: string | null }
  | { type: 'SET_COURSES_FILTER_MODE'; payload: 'pillars' | 'community' }
  | { type: 'SET_COURSES_PILLAR'; payload: string | null }
  | { type: 'SET_COURSES_TIER'; payload: string | null }
  | { type: 'RESET_SETTINGS' }
  | { type: 'LOAD_SETTINGS'; payload: Partial<Settings> };

// Default settings
const defaultSettings: Settings = {
  theme: 'dark',
  sidebarCollapsed: false,
  compactMode: false,
  notifications: {
    email: true,
    push: true,
    desktop: false,
  },
  accessibility: {
    reducedMotion: false,
    highContrast: false,
  },
  libraryTierFilter: null,
  coursesFilterMode: 'pillars',
  coursesPillar: null,
  coursesTier: null,
};

// Local storage key
const SETTINGS_STORAGE_KEY = 'prometheus-settings';

// One-time migration guard. Bump the version to force a settings reset for every
// browser on next load. Current reset: clear a persisted Learn `coursesPillar`
// so users silently trapped behind a stuck pillar filter (e.g. "Management",
// which server-scopes the course fetch and hides every other pillar) land back
// on "All Courses". `coursesTier` is left alone — it already self-heals (see the
// fallback effect in Learn.tsx when the persisted tier isn't in the loaded set).
const SETTINGS_MIGRATION_KEY = 'prometheus-settings-migration';
const SETTINGS_MIGRATION_VERSION = '2026-07-pillar-reset';

// Load settings from localStorage
function loadSettingsFromStorage(): Partial<Settings> {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load settings from localStorage:', error);
  }
  return {};
}

// Save settings to localStorage
function saveSettingsToStorage(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings to localStorage:', error);
  }
}

// Reducer
function settingsReducer(state: Settings, action: SettingsAction): Settings {
  switch (action.type) {
    case 'SET_THEME':
      return { ...state, theme: action.payload };

    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };

    case 'SET_SIDEBAR_COLLAPSED':
      return { ...state, sidebarCollapsed: action.payload };

    case 'TOGGLE_COMPACT_MODE':
      return { ...state, compactMode: !state.compactMode };

    case 'SET_NOTIFICATION':
      return {
        ...state,
        notifications: {
          ...state.notifications,
          [action.payload.key]: action.payload.value,
        },
      };

    case 'SET_ACCESSIBILITY':
      return {
        ...state,
        accessibility: {
          ...state.accessibility,
          [action.payload.key]: action.payload.value,
        },
      };

    case 'SET_LIBRARY_TIER_FILTER':
      return { ...state, libraryTierFilter: action.payload };

    case 'SET_COURSES_FILTER_MODE':
      return { ...state, coursesFilterMode: action.payload };

    case 'SET_COURSES_PILLAR':
      return { ...state, coursesPillar: action.payload };

    case 'SET_COURSES_TIER':
      return { ...state, coursesTier: action.payload };

    case 'RESET_SETTINGS':
      return defaultSettings;

    case 'LOAD_SETTINGS':
      return { ...state, ...action.payload };

    default:
      return state;
  }
}

// Context type
interface SettingsContextType {
  settings: Settings;
  // Theme actions
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  resolvedTheme: 'dark' | 'light'; // Resolved system theme
  // Sidebar actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  // Other actions
  toggleCompactMode: () => void;
  setNotification: (key: keyof Settings['notifications'], value: boolean) => void;
  setAccessibility: (key: keyof Settings['accessibility'], value: boolean) => void;
  setLibraryTierFilter: (code: string | null) => void;
  setCoursesFilterMode: (mode: 'pillars' | 'community') => void;
  setCoursesPillar: (value: string | null) => void;
  setCoursesTier: (code: string | null) => void;
  resetSettings: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Get system theme preference
function getSystemTheme(): 'dark' | 'light' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  // Initialize state with stored settings merged with defaults
  const [settings, dispatch] = useReducer(settingsReducer, defaultSettings, (initial) => {
    const stored = loadSettingsFromStorage();
    const merged = { ...initial, ...stored };
    // One-time reset (see SETTINGS_MIGRATION_VERSION): clear any stuck Learn
    // pillar filter once per browser, then stamp the version so it never re-runs.
    try {
      if (localStorage.getItem(SETTINGS_MIGRATION_KEY) !== SETTINGS_MIGRATION_VERSION) {
        merged.coursesPillar = null;
        localStorage.setItem(SETTINGS_MIGRATION_KEY, SETTINGS_MIGRATION_VERSION);
      }
    } catch {
      // localStorage unavailable (SSR / private mode) — nothing was persisted anyway.
    }
    return merged;
  });

  // Track system theme for 'system' setting
  const [systemTheme, setSystemTheme] = React.useState<'dark' | 'light'>(getSystemTheme);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Persist settings to localStorage whenever they change
  useEffect(() => {
    saveSettingsToStorage(settings);
  }, [settings]);

  // Apply theme to document
  useEffect(() => {
    const resolvedTheme = settings.theme === 'system' ? systemTheme : settings.theme;
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark');

    // Add current theme class
    root.classList.add(resolvedTheme);

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', resolvedTheme === 'dark' ? '#09090b' : '#ffffff');
    }
  }, [settings.theme, systemTheme]);

  // Apply reduced motion preference
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--motion-duration',
      settings.accessibility.reducedMotion ? '0ms' : '200ms'
    );
  }, [settings.accessibility.reducedMotion]);

  // Resolved theme (accounts for 'system' setting)
  const resolvedTheme = settings.theme === 'system' ? systemTheme : settings.theme;

  // Actions
  const setTheme = useCallback((theme: Theme) => {
    dispatch({ type: 'SET_THEME', payload: theme });
  }, []);

  const toggleTheme = useCallback(() => {
    const themes: Theme[] = ['dark', 'light', 'system'];
    const currentIndex = themes.indexOf(settings.theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    dispatch({ type: 'SET_THEME', payload: themes[nextIndex] });
  }, [settings.theme]);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    dispatch({ type: 'SET_SIDEBAR_COLLAPSED', payload: collapsed });
  }, []);

  const toggleCompactMode = useCallback(() => {
    dispatch({ type: 'TOGGLE_COMPACT_MODE' });
  }, []);

  const setNotification = useCallback((key: keyof Settings['notifications'], value: boolean) => {
    dispatch({ type: 'SET_NOTIFICATION', payload: { key, value } });
  }, []);

  const setAccessibility = useCallback((key: keyof Settings['accessibility'], value: boolean) => {
    dispatch({ type: 'SET_ACCESSIBILITY', payload: { key, value } });
  }, []);

  const setLibraryTierFilter = useCallback((code: string | null) => {
    dispatch({ type: 'SET_LIBRARY_TIER_FILTER', payload: code });
  }, []);

  const setCoursesFilterMode = useCallback((mode: 'pillars' | 'community') => {
    dispatch({ type: 'SET_COURSES_FILTER_MODE', payload: mode });
  }, []);

  const setCoursesPillar = useCallback((value: string | null) => {
    dispatch({ type: 'SET_COURSES_PILLAR', payload: value });
  }, []);

  const setCoursesTier = useCallback((code: string | null) => {
    dispatch({ type: 'SET_COURSES_TIER', payload: code });
  }, []);

  const resetSettings = useCallback(() => {
    dispatch({ type: 'RESET_SETTINGS' });
  }, []);

  const value: SettingsContextType = {
    settings,
    setTheme,
    toggleTheme,
    resolvedTheme,
    toggleSidebar,
    setSidebarCollapsed,
    toggleCompactMode,
    setNotification,
    setAccessibility,
    setLibraryTierFilter,
    setCoursesFilterMode,
    setCoursesPillar,
    setCoursesTier,
    resetSettings,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsContext;
